import { forwardRef, useRef, useState, useEffect } from 'react'
import { useTelegram } from '@/shared/lib/telegram'
import { cn } from '@/shared/lib/utils'

export interface SliderProps {
  /**
   * Текущее значение слайдера
   */
  value: number
  /**
   * Обработчик изменения значения
   */
  onChange: (value: number) => void
  /**
   * Минимальное значение
   * @default 0
   */
  min?: number
  /**
   * Максимальное значение
   * @default 100
   */
  max?: number
  /**
   * Шаг изменения значения
   * @default 1
   */
  step?: number
  /**
   * Отключен ли слайдер
   */
  disabled?: boolean
  /**
   * Дополнительные классы
   */
  className?: string
}

/**
 * UI компонент: Слайдер (бегунок)
 * 
 * Компонент для выбора числового значения путем перетаскивания.
 * Оптимизирован для touch взаимодействия.
 * 
 * @example
 * ```tsx
 * <Slider
 *   value={rating}
 *   onChange={setRating}
 *   min={0}
 *   max={10}
 *   step={1}
 * />
 * ```
 */
export const Slider = forwardRef<HTMLDivElement, SliderProps>(
  (
    {
      value,
      onChange,
      min = 0,
      max = 100,
      step = 1,
      disabled = false,
      className,
    },
    ref
  ) => {
    const { hapticFeedback } = useTelegram()
    const trackRef = useRef<HTMLDivElement>(null)
    const [isDragging, setIsDragging] = useState(false)
    const [lastValue, setLastValue] = useState(value)

    // Нормализуем значение в диапазон [min, max]
    const normalizedValue = Math.max(min, Math.min(max, value))
    const percentage = ((normalizedValue - min) / (max - min)) * 100

    const updateValue = (clientX: number) => {
      if (!trackRef.current || disabled) return

      const rect = trackRef.current.getBoundingClientRect()
      const x = clientX - rect.left
      const width = rect.width
      const percentage = Math.max(0, Math.min(1, x / width))
      const rawValue = min + percentage * (max - min)
      const steppedValue = Math.round(rawValue / step) * step
      const clampedValue = Math.max(min, Math.min(max, steppedValue))

      if (clampedValue !== lastValue) {
        setLastValue(clampedValue)
        onChange(clampedValue)
        hapticFeedback.impactOccurred('light')
      }
    }

    const handleMouseDown = (e: React.MouseEvent) => {
      if (disabled) return
      e.preventDefault()
      setIsDragging(true)
      updateValue(e.clientX)
    }

    const handleTouchStart = (e: React.TouchEvent) => {
      if (disabled) return
      e.preventDefault()
      setIsDragging(true)
      updateValue(e.touches[0].clientX)
    }

    useEffect(() => {
      if (!isDragging) return

      const handleMouseMove = (e: MouseEvent) => {
        updateValue(e.clientX)
      }

      const handleTouchMove = (e: TouchEvent) => {
        e.preventDefault()
        updateValue(e.touches[0].clientX)
      }

      const handleEnd = () => {
        setIsDragging(false)
      }

      document.addEventListener('mousemove', handleMouseMove)
      document.addEventListener('touchmove', handleTouchMove, { passive: false })
      document.addEventListener('mouseup', handleEnd)
      document.addEventListener('touchend', handleEnd)

      return () => {
        document.removeEventListener('mousemove', handleMouseMove)
        document.removeEventListener('touchmove', handleTouchMove)
        document.removeEventListener('mouseup', handleEnd)
        document.removeEventListener('touchend', handleEnd)
      }
    }, [isDragging])

    return (
      <div
        ref={ref}
        className={cn('w-full', className)}
      >
        <div
          ref={trackRef}
          className={cn(
            'relative w-full h-2 bg-gray-200 rounded-full cursor-pointer',
            disabled && 'opacity-50 cursor-not-allowed'
          )}
          onMouseDown={handleMouseDown}
          onTouchStart={handleTouchStart}
        >
          {/* Активная часть */}
          <div
            className="absolute left-0 top-0 h-full bg-primary rounded-full transition-all duration-150"
            style={{ width: `${percentage}%` }}
          />

          {/* Handle */}
          <div
            className={cn(
              'absolute top-1/2 -translate-y-1/2 w-6 h-6 bg-primary rounded-full shadow-md',
              'transition-all duration-150 touch-target',
              'focus:outline-none',
              isDragging && 'scale-110',
              disabled && 'cursor-not-allowed'
            )}
            style={{ left: `calc(${percentage}% - 12px)` }}
          />
        </div>
      </div>
    )
  }
)

Slider.displayName = 'Slider'

